import React, { useState } from 'react';
import { X, Search, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Prospect = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  company?: string | null;
  status: string;
  assignedSalespersonId?: string | null;
  assignedTo?: string;
};

const prospectName = (p: Prospect) =>
  `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email;

type TeamMember = {
  id: string;
  name: string;
  role: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (selectedProspectIds: string[]) => void;
  teamMember: TeamMember;
  allProspects: Prospect[];
  assignedProspectIds: string[];
};

const AssignProspectsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onAssign,
  teamMember,
  allProspects,
  assignedProspectIds
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'teams' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProspects, setSelectedProspects] = useState<string[]>([]);

  if (!isOpen) return null;

  const availableProspects = allProspects.filter(p => !assignedProspectIds.includes(p.id));

  const search = searchTerm.toLowerCase();
  const filteredProspects = availableProspects.filter(prospect =>
    prospectName(prospect).toLowerCase().includes(search) ||
    (prospect.email ?? '').toLowerCase().includes(search) ||
    (prospect.company ?? '').toLowerCase().includes(search)
  );

  const toggleProspect = (prospectId: string) => {
    setSelectedProspects(prev =>
      prev.includes(prospectId)
        ? prev.filter(id => id !== prospectId)
        : [...prev, prospectId]
    );
  };

  const handleAssign = () => {
    if (selectedProspects.length > 0) {
      onAssign(selectedProspects);
      setSelectedProspects([]);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setSelectedProspects([]);
    setSearchTerm('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Assign Prospects</h2>
            <p className="text-sm text-gray-600 mt-1">
              Assign prospects to {teamMember.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search prospects by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {selectedProspects.length > 0 && (
            <div className="mt-3 text-sm text-gray-600">
              {selectedProspects.length} prospect{selectedProspects.length > 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {filteredProspects.length > 0 ? (
            <div className="space-y-2">
              {filteredProspects.map((prospect) => {
                const isSelected = selectedProspects.includes(prospect.id);
                return (
                  <div
                    key={prospect.id}
                    onClick={() => toggleProspect(prospect.id)}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <h4 className="font-semibold text-gray-900">{prospectName(prospect)}</h4>
                          {prospect.status && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {prospect.status.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{prospect.company}</p>
                        <p className="text-sm text-gray-500 mt-1">{prospect.email}</p>
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-6 h-6 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm ? 'No prospects found matching your search' : 'No available prospects to assign'}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={selectedProspects.length === 0}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedProspects.length > 0
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Assign {selectedProspects.length > 0 ? `(${selectedProspects.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignProspectsModal;
